import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { faEnvelope, faIdCard, faLock, faPhone, faUser } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import { PATH_MAP } from "../../configuration/PathMap"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { useResellerLogged } from "../../context/useResellerContext"
import { useResellerAuth } from "../../hooks/Reseller/useResellerAuth"
import { useGetResellerProfile } from "../../hooks/Reseller/useGetResellerProfile"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import InputField from "../../components/InputField/InputField"
import Navbar from "../../components/LandPage/Navbar"
import logo from "../../assets/images/Logo2.png"

export const ResellerSignUpPage: React.FC = () => {
  const { t } = useTranslation("reseller")

  const [name, setName] = useState("")

  const [email, setEmail] = useState("")

  const [password, setPassword] = useState("")

  const [phoneNumber, setPhoneNumber] = useState("")

  const [documentCpfCnpj, setDocumentCpfCnpj] = useState("")

  const navigate = useNavigate()

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const { setResellerProfile } = useResellerLogged()

  const { handleSignUpReseller } = useResellerAuth()

  const { handleGetResellerProfile } = useGetResellerProfile()

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!name.trim() || !email.trim() || !password.trim() || !phoneNumber.trim() || !documentCpfCnpj.trim()) {
      setError(t("signup.error_title"), t("signup.error_required_fields"))

      return
    }

    enableLoad()

    try {
      await handleSignUpReseller({ name, email, password, phoneNumber, documentCpfCnpj })

      await handleGetResellerProfile(setResellerProfile)

      navigate(PATH_MAP.RESELLER_DASHBOARD_PAGE, { replace: true })
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("signup.error_title")

      setError(t("signup.error_title"), message)
    } finally {
      disableLoad()
    }
  }

  return (
    <Animate type={AnimationTypes.slideDownIn} duration="0.5s">
      <div className="min-h-screen flex flex-col items-center justify-center relative bg-white overflow-hidden">
        <Navbar showFunctions={false} showWhatsAppFloat={false} />

        <div className="w-full max-w-md p-8 md:p-6 mt-10">
          <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
            <div className="mb-6 flex flex-col items-center text-center">
              <img src={logo} alt="Logo Catalojei" className="mb-4 w-52 drop-shadow-xl md:w-64" />

              <h1 className="text-2xl font-bold text-gray-700 md:text-2xl">{t("signup.title")}</h1>

              <p className="mt-2 max-w-sm text-sm leading-relaxed font-bold text-gray-500">{t("signup.subtitle")}</p>
            </div>
          </Animate>

          <Animate type={AnimationTypes.slideUpIn} duration="1.8s">
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <InputField
                  icon={faUser}
                  value={name}
                  type="text"
                  placeholder={t("signup.label_name_placeholder")}
                  animationTypes={AnimationTypes.slideUpIn}
                  onChange={(e) => setName(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.reseller.signup.input.name"
                />

                <InputField
                  icon={faEnvelope}
                  value={email}
                  type="email"
                  placeholder={t("signup.label_email_placeholder")}
                  animationTypes={AnimationTypes.slideUpIn}
                  onChange={(e) => setEmail(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.reseller.signup.input.email"
                />

                <InputField
                  icon={faLock}
                  value={password}
                  type="password"
                  placeholder={t("signup.label_password_placeholder")}
                  animationTypes={AnimationTypes.slideUpIn}
                  onChange={(e) => setPassword(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.reseller.signup.input.password"
                />

                <InputField
                  icon={faPhone}
                  value={phoneNumber}
                  type="text"
                  placeholder={t("signup.label_phone_placeholder")}
                  animationTypes={AnimationTypes.slideUpIn}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.reseller.signup.input.phoneNumber"
                />

                <InputField
                  icon={faIdCard}
                  value={documentCpfCnpj}
                  type="text"
                  placeholder={t("signup.label_document_placeholder")}
                  animationTypes={AnimationTypes.slideUpIn}
                  onChange={(e) => setDocumentCpfCnpj(e.target.value)}
                  descriptionKey="interactionDescriptions:pages.reseller.signup.input.document"
                />
              </div>

              <button
                type="submit"
                className="mt-6 w-full rounded-md bg-gradient-to-r from-orange-500 to-red-500 px-4 py-3 text-sm font-bold text-white shadow-lg transition-all duration-200 hover:scale-[1.01] hover:from-orange-400 hover:to-red-400"
                data-interaction-description-key="interactionDescriptions:pages.reseller.signup.button.submit"
              >
                {t("signup.button_submit")}
              </button>

              <button
                type="button"
                className="mt-4 w-full text-center text-sm font-semibold text-gray-500 hover:text-gray-700"
                onClick={() => navigate(PATH_MAP.RESELLER_LOGIN_PAGE)}
                data-interaction-description-key="interactionDescriptions:pages.reseller.signup.button.loginLink"
              >
                {t("signup.login_link")}
              </button>
            </form>
          </Animate>
        </div>
      </div>
    </Animate>
  )
}
